import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { listFeedbackAdmin, updateFeedbackAdmin } from "@/lib/journey.functions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/feedback")({
  component: AdminFeedback,
});

type Row = Awaited<ReturnType<typeof listFeedbackAdmin>>[number];

function AdminFeedback() {
  const load = useServerFn(listFeedbackAdmin);
  const upd = useServerFn(updateFeedbackAdmin);
  const [rows, setRows] = useState<Row[]>([]);
  const [notes, setNotes] = useState<Record<string, string>>({});

  const refresh = () => load().then(setRows).catch((e) => toast.error((e as Error).message));
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const save = async (id: string, status: string) => {
    await upd({ data: { id, status, admin_note: notes[id] } });
    toast.success("Updated");
    refresh();
  };

  return (
    <div className="min-h-dvh bg-obsidian text-foreground">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <h1 className="font-serif text-3xl text-gold-luminous">Feedback</h1>
        <p className="mt-1 text-sm text-foreground/70">All member and visitor feedback submissions.</p>
        <ul className="mt-6 space-y-3">
          {rows.map((r) => (
            <li key={r.id}>
              <Card className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-foreground/50">{r.kind} · {r.status}</div>
                    <div className="font-medium">{r.subject}</div>
                    <div className="text-xs text-foreground/50">{new Date(r.created_at).toLocaleString()} · {r.page_path ?? "—"}</div>
                  </div>
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm text-foreground/80">{r.message}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Input
                    placeholder="Admin note"
                    defaultValue={r.admin_note ?? ""}
                    onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))}
                    className="max-w-md"
                  />
                  <Button size="sm" variant="outline" onClick={() => save(r.id, "acknowledged")}>Acknowledge</Button>
                  <Button size="sm" variant="outline" onClick={() => save(r.id, "resolved")}>Resolve</Button>
                  <Button size="sm" variant="ghost" onClick={() => save(r.id, "archived")}>Archive</Button>
                </div>
              </Card>
            </li>
          ))}
          {rows.length === 0 && <p className="text-sm text-foreground/60">No feedback yet.</p>}
        </ul>
      </div>
    </div>
  );
}
